/**
 * The quick note pin (Part 09 decisions 3 and 6, mockup `notePin`, `.qn`):
 * a calm "New quick note" tile whose field opens in place on hover or
 * focus. ⌘/Ctrl ↵, Save, Esc or a click away saves a non-empty draft as a
 * note frame of the project (the doc gate may make it a doc), which arrives
 * at the top of the feed; nothing opens. An empty draft is dropped. While
 * it saves the field is read-only; a failure keeps the draft (the note
 * creator shows the error).
 */
import { useRef, useState, type KeyboardEvent } from "react";
import { createDocFrame } from "@/components/layout/create-doc-frame";
import { Button } from "@/components/ui";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { wallMessages } from "@/i18n/messages/wall";
import { isMacPlatform } from "@/lib/os-platform";
import { useProjectStore } from "@/stores/project-store";
import { WallPinTile } from "./wall-pin-tile";

const SAVE_SHORTCUT = isMacPlatform() ? "⌘↵" : "Ctrl ↵";

function keepSelection(): void {}

export function WallQuickNoteTile({ projectId }: { projectId: string }) {
  const t = useT(wallMessages);
  const tCommon = useT(commonMessages);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  // A blur and a Save click land together; only the first one saves.
  const inFlight = useRef(false);

  const save = async () => {
    const text = draft.trim();
    if (inFlight.current) return;
    if (!text) {
      setDraft("");
      return;
    }
    const spaceId = useProjectStore.getState().currentSpace?.id;
    if (!spaceId) return;
    inFlight.current = true;
    setSaving(true);
    const frame = await createDocFrame({
      projectId,
      spaceId,
      text,
      selectFrame: keepSelection,
    });
    inFlight.current = false;
    setSaving(false);
    if (frame) setDraft("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void save();
      return;
    }
    if (event.key === "Escape") {
      // The wall's own Esc (a selection) waits; leaving the field saves.
      event.stopPropagation();
      event.currentTarget.blur();
    }
  };

  return (
    <WallPinTile
      pin="note"
      label={t("newQuickNote")}
      bodyClassName="focus-within:ring-2 focus-within:ring-accent"
    >
      {/* A draft keeps the field open (`wall-surface.css`). */}
      <div
        className="flex h-full flex-col gap-[0.6em] p-[1.2em]"
        data-typing={draft !== "" || undefined}
      >
        <span className="text-[0.7em] font-medium uppercase tracking-[0.1em] text-muted">
          {t("newQuickNote")}
        </span>
        <textarea
          value={draft}
          readOnly={saving}
          placeholder={t("startTyping")}
          aria-label={t("newQuickNote")}
          className="wall-quick-note-field -mx-[0.5em] min-h-0 w-[calc(100%+1em)] flex-1 resize-none rounded-[12px] border-0 bg-transparent px-[0.5em] py-[0.3em] font-display text-[1.3em] font-medium leading-[1.25] tracking-[-0.02em] text-primary outline-none placeholder:text-muted placeholder:opacity-80 motion-safe:transition-[background-color] motion-safe:duration-[180ms]"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => void save()}
          data-testid="wall-quick-note"
        />
        <div className="wall-quick-note-footer pointer-events-none flex translate-y-[4px] items-center gap-[0.6em] text-[0.74em] text-muted opacity-0 motion-safe:transition-[opacity,transform] motion-safe:duration-[180ms]">
          <span className="min-w-0 flex-1 truncate">
            {t("quickNoteSaveHint", { shortcut: SAVE_SHORTCUT })}
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-[2.2em] bg-card-ink px-[1em] text-[1em] font-semibold text-card-surface"
            disabled={saving}
            onClick={() => void save()}
            data-testid="wall-quick-note-save"
          >
            {tCommon("save")}
          </Button>
        </div>
      </div>
    </WallPinTile>
  );
}
